// Pure mapping + validation between the builder form state and the two
// jsonb columns on ai_agent_config: laya_config and llm_config.
//
// Plain JS on purpose: it is imported by the React page AND run directly by
// node in layaLlm.selfcheck.mjs (the repo has no test runner installed).

export const LAYA_DEFAULTS = { mode: 'classify', contextTurns: 0, lang: 'en', answerThreshold: 0.45 };
export const LLM_DEFAULTS = { temperature: 0.2, maxTokens: 180 };

export const OTHER_DESCRIPTION =
  'booking or changing a booking, prices, availability, personal details, complaints or problems, or anything not listed above';

export const RESPONSE_STYLE_PRESETS = {
  Concise: 'Be clear, short and confident. One or two sentences. No filler.',
  Warm: 'Be warm and reassuring. Short sentences, friendly words, never pushy.',
  Formal: 'Be polite and professional. Full sentences, no slang, no contractions.',
};

const uid = (p) => `${p}-${Math.random().toString(36).slice(2, 9)}`;

/** "asks about check-in time" -> "asks_about_check_in_time" (snake_case label). */
export function toSnakeCase(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

// ---------------------------------------------------------------- laya

export function emptyLayaState(firstLang) {
  return {
    enabled: false,
    name: '',
    description: 'Common caller questions answered without the LLM',
    mode: LAYA_DEFAULTS.mode,
    contextTurns: LAYA_DEFAULTS.contextTurns,
    lang: firstLang || LAYA_DEFAULTS.lang,
    answers: [],
    otherDescription: OTHER_DESCRIPTION,
  };
}

/** laya_config (or null, for agents saved before this feature) -> form state. */
export function layaConfigToState(config, firstLang) {
  const base = emptyLayaState(firstLang);
  if (!config || typeof config !== 'object') return base;

  const prompts = Array.isArray(config.prompts) ? config.prompts : [];
  const other = prompts.find((p) => p?.label === 'other' || p?.action === 'llm');

  return {
    ...base,
    enabled: config.config?.enabled !== false && prompts.length > 0,
    name: config.name || base.name,
    description: config.description || base.description,
    mode: config.type?.mode || base.mode,
    contextTurns: Number(config.type?.context_turns ?? base.contextTurns),
    lang: config.config?.lang || base.lang,
    otherDescription: other?.description || base.otherDescription,
    answers: prompts
      .filter((p) => p && p.action === 'answer')
      .map((p, i) => ({
        id: `laya-${i}`,
        label: p.label || toSnakeCase(p.description) || `answer_${i + 1}`,
        description: p.description || '',
        answer: p.answer || '',
        enabled: p.enabled !== false,
      })),
  };
}

/**
 * Form state -> laya_config. Returns null only when the user never touched the
 * section (nothing to keep). Pausing never nulls the column: the prompts stay
 * and only config.enabled flips.
 */
export function stateToLayaConfig(state, agentName) {
  if (!state) return null;
  const answers = (state.answers || []).filter((a) => a.description?.trim() || a.answer?.trim());
  if (answers.length === 0 && !state.enabled) return null;

  // labels are the routing key: they must be unique and must never shadow
  // `other`. Unicode questions squash to an empty slug, so fall back by index.
  const taken = new Set(['other']);
  const uniqueLabel = (answer, i) => {
    const base = answer.label?.trim() || toSnakeCase(answer.description) || `answer_${i + 1}`;
    let label = taken.has(base) ? `${base}_2` : base;
    for (let n = 3; taken.has(label); n += 1) label = `${base}_${n}`;
    taken.add(label);
    return label;
  };

  return {
    name: state.name?.trim() || `${agentName || 'agent'} faq`.toLowerCase(),
    description: state.description?.trim() || 'Common caller questions answered without the LLM',
    type: { mode: state.mode || LAYA_DEFAULTS.mode, context_turns: Number(state.contextTurns) || 0 },
    config: {
      lang: state.lang || LAYA_DEFAULTS.lang,
      enabled: !!state.enabled,
      // server default 0.90 is too strict for a classify set: Laya would never answer
      thresholds: { answer: LAYA_DEFAULTS.answerThreshold },
    },
    prompts: [
      ...answers.map((a, i) => ({
        label: uniqueLabel(a, i),
        action: 'answer',
        description: a.description?.trim() || '',
        answer: a.answer?.trim() || '',
        // paused answers are kept, never dropped
        ...(a.enabled === false ? { enabled: false } : {}),
      })),
      // the `other` row is fixed, last, and always saved
      { label: 'other', action: 'llm', description: state.otherDescription?.trim() || OTHER_DESCRIPTION },
    ],
  };
}

const FILLER = new Set(('a an the is are am do does did can could will would i you we me my your our it its to of for on in at by and or if '
  + 'ask asks asking about what whats when where how which who there any have has available want wants need tell please caller').split(' '));
const keywords = (text) =>
  new Set(String(text || '').toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 1 && !FILLER.has(w)));

/**
 * Answers whose "when the caller asks" lines say nearly the same thing: the
 * classifier cannot tell them apart, so callers get the wrong reply.
 * @returns {Object<string, string>} answer id -> the other answer's line it overlaps with
 */
export function overlappingAnswers(answers) {
  const list = (answers || []).filter((a) => a.description?.trim());
  const out = {};
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = keywords(list[i].description);
      const b = keywords(list[j].description);
      const shared = [...a].filter((w) => b.has(w)).length;
      const smaller = Math.min(a.size, b.size);
      if (shared >= 1 && smaller > 0 && shared / smaller >= 0.5) {
        out[list[i].id] ||= list[j].description.trim();
        out[list[j].id] ||= list[i].description.trim();
      }
    }
  }
  return out;
}

export function newLayaAnswer() {
  return { id: uid('laya'), label: '', description: '', answer: '', enabled: true };
}

/** @returns {{errors: string[], warnings: string[]}} errors block saving. */
export function validateLaya(state) {
  const errors = [];
  const warnings = [];
  if (!state?.enabled) return { errors, warnings };

  // switched on but empty would save a dead config: block it
  const filled = (state.answers || []).filter((a) => a.description?.trim() || a.answer?.trim());
  if (filled.length === 0) {
    errors.push('Fast answers is on but has no answers. Add one, or turn it off.');
  }

  const seen = new Set();
  (state.answers || []).forEach((a, i) => {
    const where = a.description?.trim() ? `"${a.description.trim()}"` : `answer ${i + 1}`;
    // real callers heard "<address, landmark>" read out loud. Hard block.
    if (/[<>]/.test(a.answer || '')) {
      errors.push(`Remove the < > placeholder from ${where} — callers hear it read out loud.`);
    }
    if (a.answer?.trim() && !a.description?.trim()) errors.push(`Answer ${i + 1} needs a "when the caller asks…" line.`);
    else if (!a.description?.trim()) { /* blank row, ignored on save */ }
    else if (seen.has(a.description.trim().toLowerCase())) warnings.push(`Two answers both cover ${where}.`);
    else seen.add(a.description.trim().toLowerCase());
    if (a.description?.trim() && !a.answer?.trim()) errors.push(`${where} needs something to say back.`);
    if ((a.answer || '').length > 240) warnings.push(`${where} is long — keep it to two short sentences.`);
  });

  if ((state.answers || []).length > 15) {
    warnings.push('More than 15 fast answers makes picking the right one less reliable.');
  }
  return { errors, warnings };
}

// ---------------------------------------------------------------- llm

export function emptyLlmState() {
  return {
    enabled: false,
    name: '',
    instructions: '',
    temperature: LLM_DEFAULTS.temperature,
    maxTokens: LLM_DEFAULTS.maxTokens,
    escalation: '',
    clarify: '',
    confirmCompletion: '',
  };
}

const promptText = (prompts, type) =>
  (Array.isArray(prompts) ? prompts : []).find((p) => p?.type === type)?.text || '';

export function llmConfigToState(config) {
  const base = emptyLlmState();
  if (!config || typeof config !== 'object') return base;
  return {
    ...base,
    enabled: config.config?.enabled !== false,
    name: config.name || '',
    instructions: config.instructions || '',
    temperature: Number(config.config?.temperature ?? base.temperature),
    maxTokens: Number(config.config?.max_tokens ?? base.maxTokens),
    escalation: promptText(config.prompts, 'escalation'),
    clarify: promptText(config.prompts, 'clarify'),
    confirmCompletion: promptText(config.prompts, 'confirm_completion'),
  };
}

/** Form state -> llm_config. null when nothing is set at all. */
export function stateToLlmConfig(state, agentName) {
  if (!state) return null;
  const prompts = [
    { type: 'escalation', text: state.escalation },
    { type: 'clarify', text: state.clarify },
    { type: 'confirm_completion', text: state.confirmCompletion },
  ]
    .filter((p) => p.text?.trim())
    .map((p) => ({ type: p.type, text: p.text.trim() }));

  const touched = !!state.instructions?.trim() || prompts.length > 0 || state.enabled;
  if (!touched) return null;

  return {
    name: state.name?.trim() || `${agentName || 'agent'} llm`.toLowerCase(),
    instructions: state.instructions?.trim() || '',
    config: {
      temperature: Number(state.temperature ?? LLM_DEFAULTS.temperature),
      max_tokens: Number(state.maxTokens ?? LLM_DEFAULTS.maxTokens),
      enabled: !!state.enabled,
    },
    prompts,
  };
}

export function validateLlm(state) {
  const errors = [];
  const warnings = [];
  if (!state?.enabled) return { errors, warnings };
  if (state.clarify?.trim() && !state.clarify.includes('{meaning}')) {
    errors.push('The clarify prompt must include the {meaning} chip.');
  }
  if (state.confirmCompletion?.trim() && !state.confirmCompletion.includes('{completion_type}')) {
    errors.push('The confirm prompt must include the {completion_type} chip.');
  }
  const t = Number(state.temperature);
  if (t < 0 || t > 1) errors.push('Temperature must be between 0 and 1.');
  const m = Number(state.maxTokens);
  if (m < 60 || m > 400) errors.push('Max reply length must be between 60 and 400.');
  return { errors, warnings };
}
